import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import type {AssistantFeedbackReasonEnum} from 'src/api/ProactApiClient';
import {AssistantFeedbackRatingEnum} from 'src/api/ProactApiClient';
import type {ControlCenterView, FeedbackUrlFilters} from './controlCenterParams';
import {
  VIEW_PARAM,
  normalizeFeedbackFilters,
  readControlCenterView,
  readFeedbackFilters,
  writeFeedbackFilters,
} from './controlCenterParams';

export interface UseControlCenterViewResult {
  view: ControlCenterView;
  selectView: (view: string) => void;
}

/** The Control Center tab lives in `?view=` so Overview links can deep-link a tab. */
export const useControlCenterView = (): UseControlCenterViewResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = readControlCenterView(searchParams);
  const selectView = useCallback(
    (next: string) =>
      setSearchParams(prev => {
        const updated = new URLSearchParams(prev);
        updated.set(VIEW_PARAM, next);
        return updated;
      }),
    [setSearchParams]
  );
  return {view, selectView};
};

export interface UseFeedbackUrlFiltersResult extends FeedbackUrlFilters {
  /** `undefined` = all ratings; choosing Up clears the reasons. */
  setRating: (rating: AssistantFeedbackRatingEnum | undefined) => void;
  /** Any selected reason also selects Down. */
  setReasons: (reasons: AssistantFeedbackReasonEnum[]) => void;
}

export const useFeedbackUrlFilters = (): UseFeedbackUrlFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => readFeedbackFilters(searchParams),
    [searchParams]
  );

  const update = useCallback(
    (patch: (current: FeedbackUrlFilters) => FeedbackUrlFilters) =>
      setSearchParams(
        prev => writeFeedbackFilters(prev, patch(readFeedbackFilters(prev))),
        {replace: true}
      ),
    [setSearchParams]
  );

  const setRating = useCallback(
    (rating: AssistantFeedbackRatingEnum | undefined) =>
      update(current => ({
        rating,
        reasons:
          rating === AssistantFeedbackRatingEnum.Down ? current.reasons : [],
      })),
    [update]
  );

  const setReasons = useCallback(
    (reasons: AssistantFeedbackReasonEnum[]) =>
      update(current =>
        normalizeFeedbackFilters({rating: current.rating, reasons})
      ),
    [update]
  );

  return {...filters, setRating, setReasons};
};
